import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const typeToSeverity = {
  survey_available: 'info',
  reward_approved: 'success',
  moderation_warning: 'warning',
  withdrawal_processed: 'success',
  reward_shipped: 'info',
  response_approved: 'success',
  response_rejected: 'error',
  reward_claimed: 'info',
  reward_claim_request: 'info',
  withdrawal_request: 'info'
};

export default function LiveNotifications() {
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const shownIdsRef = useRef(new Set());
  const lastSeenRef = useRef(null);
  const navigate = useNavigate();

  // Decide where to navigate on click
  const getNotificationsPath = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return '/login';
      const user = JSON.parse(userStr);
      if (user.role === 'admin') return '/admin/notifications';
      if (user.role === 'client') return '/client/notifications';
      return '/respondent/notifications';
    } catch {
      return '/login';
    }
  };

  useEffect(() => {
    // Initialize last seen to now on first mount to avoid spamming old items
    if (!lastSeenRef.current) {
      const persisted = localStorage.getItem('notifications:lastToastTs');
      lastSeenRef.current = persisted ? new Date(persisted) : new Date();
    }

    let intervalId;

    const poll = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.get(`${API_BASE}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = Array.isArray(res.data) ? res.data : [];
        const baseline = lastSeenRef.current ? new Date(lastSeenRef.current) : new Date(0);

        // Convert createdAt/timestamp to Date
        const withDates = list.map(n => ({
          ...n,
          _created: n.createdAt ? new Date(n.createdAt) : (n.timestamp ? new Date(n.timestamp) : new Date(0))
        }));

        // Only consider unread and newer than baseline and not shown before
        const newly = withDates
          .filter(n => n.read === false && n._created > baseline && !shownIdsRef.current.has(n.id))
          // Show oldest first
          .sort((a, b) => a._created - b._created);

        if (newly.length) {
          // Enqueue and mark as shown to avoid duplicates
          newly.forEach(n => shownIdsRef.current.add(n.id));
          setQueue(prev => [...prev, ...newly]);
          // Advance baseline to the newest we saw
          const newest = newly[newly.length - 1]._created;
          lastSeenRef.current = newest;
          localStorage.setItem('notifications:lastToastTs', newest.toISOString());
        }
      } catch (e) {
        // Silent fail; try again later
      }
    };

    // Kick off immediately and then poll
    poll();
    intervalId = setInterval(poll, 15000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!open && queue.length > 0) {
      const next = queue[0];
      setCurrent(next);
      setOpen(true);
      setQueue(prev => prev.slice(1));
    }
  }, [queue, open]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  if (!current) return null;

  const severity = typeToSeverity[current.type] || 'info';

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        variant="filled"
        sx={{ width: '100%' }}
        action={
          <Button color="inherit" size="small" onClick={handleClose}>
            Скрыть
          </Button>
        }
      >
        <strong>{current.title || 'Уведомление'}</strong>
        <div style={{ fontSize: 12, opacity: 0.9 }}>{current.message}</div>
      </Alert>
    </Snackbar>
  );
}
